/**
 * The Gateways tab's vocabulary — strategy cards (mock p2e copy), zone
 * hints, colors. Copy states the runtime honestly: since Phase 17 the
 * gateway verdict blocks a terrible route for the day and the daily capacity
 * is enforced across the country.
 * For Media Devoted | Created By Ciro Autiero
 */

export const GW_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#f59e0b',
  '#14b8a6',
  '#22c55e',
  '#64748b',
  '#f43f5e',
] as const;

export const gwColor = (index: number) => GW_COLORS[index % GW_COLORS.length];
export const gwLetter = (index: number) => String.fromCharCode(65 + (index % 26));

export const STRATEGY_LABELS: Record<string, string> = {
  HOLD_UNTIL_FAIL: 'Hold until fails',
  ALTERNATE: 'Alternate',
  RANDOM: 'Random',
  SPLIT: '% split',
  ROUTE_TEST: 'Route tests',
};

/** glyph: A-Z = gateway chip · → arrow · ! fail marker · 'bar' = share bar. */
export const STRATEGY_CARDS = [
  {
    value: 'HOLD_UNTIL_FAIL',
    title: 'Hold until fails',
    glyph: 'AAA→!→BB',
    caption: 'one gateway carries the day — next on fail',
    hint: 'One gateway carries everything until the gateway verdict blocks it (Verdicts tab) or its daily capacity is reached — then the next in order takes over, same day.',
  },
  {
    value: 'ALTERNATE',
    title: 'Alternate',
    glyph: 'ABCABC',
    caption: 'every drop takes the next in order',
    hint: 'Every drop takes the next gateway in order — the ROUTE TEST pattern.',
  },
  {
    value: 'RANDOM',
    title: 'Random',
    glyph: 'BAACAB',
    caption: 'each drop draws one by the odds',
    hint: "Each drop draws a gateway by the weights' odds. Drawn when you Generate and persisted; before that the Day timeline shows an indicative rotation.",
  },
  {
    value: 'SPLIT',
    title: '% split',
    glyph: 'bar',
    caption: 'the day divides to match the shares',
    hint: "The day's drops are divided to match the weights exactly — 50/30/20 over 10 drops is always 5 · 3 · 2.",
  },
  {
    value: 'ROUTE_TEST',
    title: 'Route tests',
    glyph: 'ABC',
    caption: 'a route test picks the gateway and sender',
    hint: 'The route test sends its test drops through its own gateways — taking turns, or all through the first one. Its first drop must be before the country window. When the test is over, the best-scored gateway and its sender go onto the other containers’ unsent drops — a ‘Use for the rest of today’ rule can require a bar first.',
  },
] as const;

export const STRATEGY_HINT =
  "How the day's drops spread over the pool's gateways. A gateway the verdict blocked, or at its daily capacity, leaves for the day; a drop that pins its own gateway keeps it.";

export const ORDER_HINT =
  'The rotation: Hold starts at the first, Alternate cycles through. Drag it by hand, or let the gateway formula order it — best score first, a gateway a rule excludes (“Not enough data”) after the ranked ones. A gateway the verdict blocked, or at its daily capacity, leaves the rotation for the day. A pinned gateway (slot row) beats the strategy.';

export const WEIGHTS_HINT =
  "Equal: every gateway gets the same share. Manual: you set each share — normalized to 100. By formula: each ranked gateway's share of the scores; one a rule excludes gets the average share so it keeps learning; no score at all → equal.";

export const CAPACITY_HINT =
  "Max SMS a day through THIS lane, counted across the whole country (a route is one pipe — every container's drops on this lane count). At the cap the lane leaves the distribution: later drops go to the next one; when every lane is full the drop is not planned, and the timeline says so. A gateway with two lanes sends the sum of their caps.";
